import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type Link from 'ember-link/link';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';
import LanguageSelector from 'garaje/components/language-selector';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiHeader from 'garaje/components/ui/header';
import preventDefault from 'garaje/helpers/prevent-default';
import type Flow from 'garaje/models/flow';
import type FlowQuestionnaire from 'garaje/models/flow-questionnaire';
import type GlobalFlow from 'garaje/models/global-flow';
import type Questionnaire from 'garaje/models/questionnaire';
import type QuestionnaireSection from 'garaje/models/questionnaire-section';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { SignInFieldKind } from 'garaje/utils/enums';
import type { Locale, LocaleKey } from 'garaje/utils/locale-options';
import QuestionnaireValidator from 'garaje/utils/questionnaire-validator';
import sortByPosition from 'garaje/utils/sort-by-position';
import { ORIGINAL_OBJECT } from 'garaje/utils/translations';
import { cached, localCopy } from 'tracked-toolbox';

import Section from './form/section';
import QuestionnaireSettingsForm from './form/settings-form';
import TitleField from './form/title';

interface VisitorsQuestionnairesFormSignature {
  Args: {
    availableLocales: Locale[];
    backLink: Link;
    canDeleteQuestionnaires: boolean;
    changedModels: Set<Model>;
    defaultLocale: LocaleKey;
    fetchFlowsPage: (page: number, searchTerm: string) => Promise<Array<Flow | GlobalFlow>>;
    flows: Flow[] | GlobalFlow[];
    onSave?: (questionnaire: Questionnaire) => void;
    questionnaire: Questionnaire;
    readOnly?: boolean;
  };
}

export default class QuestionnaireForm extends Component<VisitorsQuestionnairesFormSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: Store;

  // For new questionnaires, settings panel should be open by default. Otherwise, show it collapsed initially.
  @tracked isSettingsPanelOpen = <boolean>(this.args.questionnaire.isNew as unknown);

  @tracked isShowingDeleteConfirmation = false;

  @localCopy('args.defaultLocale') declare selectedLocale: LocaleKey;

  get hasChanges(): boolean {
    return this.args.changedModels.size > 0;
  }

  get hasMultipleSections(): boolean {
    return this.sections.length > 1;
  }

  get isSaveButtonDisabled(): boolean {
    return this.save.isRunning;
  }

  @cached
  get localizedQuestionnaire(): Questionnaire {
    return this.args.questionnaire.forLocale(this.selectedLocale, this.args.defaultLocale);
  }

  get sections(): QuestionnaireSection[] {
    return this.args.questionnaire.questionnaireSections
      .filter((section) => !section.isDeleted)
      .toSorted(sortByPosition);
  }

  get showAddSectionButton(): boolean {
    // Don't show "Add section" button if we're read-only, or the user is viewing a non-default language.
    if (this.args.readOnly) return false;
    return this.selectedLocale === this.args.defaultLocale;
  }

  get showLanguageSelector(): boolean {
    return this.args.availableLocales.length > 1;
  }

  @cached
  get validator(): QuestionnaireValidator {
    return new QuestionnaireValidator(this.args.questionnaire);
  }

  @action
  addChangedModels(models: Model[]): void {
    models.forEach((model) =>
      this.args.changedModels.add(ORIGINAL_OBJECT in model ? <Model>model[ORIGINAL_OBJECT] : model),
    );
  }

  @action
  addSection(): void {
    const lastPosition = Math.max(...this.sections.map((section) => section.position));
    const newSection = this.store.createRecord('questionnaire-section', {
      questionnaire: this.args.questionnaire,
      position: lastPosition + 1,
    });

    // Initialize new section with one empty single-response question
    const question = this.store.createRecord('questionnaire-field', {
      questionnaireSection: newSection,
      kind: SignInFieldKind.SINGLE_SELECTION,
      position: 1,
    });
    const options = [
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 1 }),
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 2 }),
    ];

    this.addChangedModels([newSection, question, ...options]);

    // Focus the "description" textarea for the new section
    const descriptionElement = document.getElementById(`description-${guidFor(newSection)}`);
    setTimeout(() => descriptionElement?.focus(), 50);
  }

  @action
  changeLocale(locale: LocaleKey): void {
    this.selectedLocale = locale;
  }

  @action
  deleteSection(section: QuestionnaireSection): void {
    section.deleteRecord();

    const changedModels = [section];

    // reorder remaining sections
    this.sections.forEach((s, index) => {
      s.position = index + 1;
      if (<boolean>(s.hasDirtyAttributes as unknown)) changedModels.push(s);
    });

    this.addChangedModels(changedModels);
  }

  @action
  async deleteQuestionnaire(): Promise<void> {
    await this.args.questionnaire.destroyRecord();
    this.flashMessages.showAndHideFlash('success', 'Assessment deleted');
    this.args.backLink.transitionTo();
  }

  @action
  hideDeleteConfirmation(): void {
    this.isShowingDeleteConfirmation = false;
  }

  @action
  markQuestionnaireAsChangedIfDirty(): void {
    const questionnaire = this.args.questionnaire;
    if (<boolean>(questionnaire.hasDirtyAttributes as unknown)) {
      this.addChangedModels([questionnaire]);
    }
  }

  @action
  setTitle(title: string): void {
    const questionnaire = this.localizedQuestionnaire;
    questionnaire.title = title;
    this.addChangedModels([questionnaire]);
  }

  @action
  showDeleteConfirmation(): void {
    this.isShowingDeleteConfirmation = true;
  }

  @action
  toggleSettingsCollapsed(): void {
    this.isSettingsPanelOpen = !this.isSettingsPanelOpen;
  }

  @action
  updateAssignedFlows(flows: Array<Flow | GlobalFlow>): void {
    const questionnaire = this.args.questionnaire;
    const currentSelectedFlows = new Set(
      questionnaire.flowQuestionnaires.filter((fq) => !fq.isDeleted).map((fq) => fq.flow),
    );
    const newSelectedFlows = new Set<Flow | GlobalFlow>(flows);

    const deletedFlows = currentSelectedFlows.difference(newSelectedFlows);
    const newFlows = newSelectedFlows.difference(currentSelectedFlows);

    for (const flow of deletedFlows) {
      const flowQuestionnaire = questionnaire.flowQuestionnaires.find((fq) => fq.flow === flow)!;
      flowQuestionnaire.deleteRecord();
      this.addChangedModels([flowQuestionnaire]);
    }

    let position = 1;
    for (const flow of newSelectedFlows) {
      let flowQuestionnaire: FlowQuestionnaire;
      if (newFlows.has(flow)) {
        flowQuestionnaire = this.store.createRecord('flow-questionnaire', {
          flow,
          questionnaire,
        });
      } else {
        flowQuestionnaire = questionnaire.flowQuestionnaires.find((fq) => fq.flow === flow)!;
      }
      flowQuestionnaire.position = position++;
      this.addChangedModels([flowQuestionnaire]);
    }
  }

  @action
  updateForceRetake(): void {
    const questionnaire = this.args.questionnaire;
    questionnaire.forceRetakeOnRevision = !questionnaire.forceRetakeOnRevision;
    this.addChangedModels([questionnaire]);
  }

  save = dropTask(async () => {
    if (!this.validator.validate()) return;

    try {
      const questionnaire = this.args.questionnaire;

      // Save models in a specific order, so that child models are created _after_ parent models.
      // This array specifies the order.
      const modelTypes = [
        'questionnaire',
        'flow-questionnaire',
        'questionnaire-section',
        'questionnaire-field',
        'questionnaire-field-option',
      ];

      // Where a model is dependent on a parent model existing, map the model to the parent model type here.
      // This is used to avoid saving child models when the parent model is deleted (i.e., if a user adds a
      // new section and questions, then deletes the section, we don't want to save the question or its options.)
      const parentModelMap: Record<string, string> = {
        'questionnaire-section': 'questionnaire',
        'questionnaire-field': 'questionnaireSection',
        'questionnaire-field-option': 'questionnaireField',
      };

      for (const modelType of modelTypes) {
        const savePromises = [...this.args.changedModels]
          // find only models of the current type that are not destroyed
          .filter((model) => (<typeof Model>model.constructor).modelName === modelType && !model.isDestroyed)
          // exclude models that expect to have a parent but don't
          .filter((model) => {
            const modelType = (<typeof Model>model.constructor).modelName as string;
            if (!parentModelMap[modelType]) return true;
            // @ts-expect-error - we want to check belongsTo but we don't have things strongly typed here
            return !!model.belongsTo(parentModelMap[modelType]).id();
          })
          .map((model) => model.save());
        await Promise.all(savePromises);
      }
      this.args.changedModels.clear();

      this.flashMessages.showAndHideFlash('success', `"${questionnaire.title}" saved!`);
      this.args.onSave?.(questionnaire);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', 'Something went wrong while saving the assessment', {
        details: 'Please try again',
      });
    }
  });

  <template>
    <form data-test-questionnaire-form {{on "submit" (preventDefault this.save.perform)}}>
      <UiHeader @backLink={{@backLink}} class="pb-8 border-b">
        <:title>
          <div class="flex items-center">
            <div class="flex-auto" data-test-questionnaire-title>
              <TitleField
                @defaultLocale={{@defaultLocale}}
                @defaultLocaleTitle={{@questionnaire.title}}
                @readOnly={{@readOnly}}
                @selectedLocale={{this.selectedLocale}}
                @title={{this.localizedQuestionnaire.title}}
                @update={{this.setTitle}}
              />
            </div>
            <div class="whitespace-nowrap">
              {{#if (and (not @questionnaire.isNew) @canDeleteQuestionnaires)}}
                <button
                  type="button"
                  class="btn btn--outline !pb-[11px] !pt-[10px]"
                  {{on "click" this.showDeleteConfirmation}}
                  data-test-delete-questionnaire
                >
                  {{svgJar "trash-delete-black" height="16" width="16"}}
                </button>
              {{/if}}
              {{#unless @readOnly}}
                <button
                  type="submit"
                  class="btn btn--brand"
                  disabled={{this.isSaveButtonDisabled}}
                  data-test-save-button
                >
                  {{if this.save.isRunning "Saving..." "Save"}}
                </button>
              {{/unless}}
            </div>
          </div>
        </:title>
      </UiHeader>

      <div class="mt-6 w-3/4 flex items-start">
        <div class="flex-1">
          <h2>{{if @readOnly "View" "Design"}} your assessment</h2>
          <p class="mt-4">
            Assessments never expire. Visitors are required to get 100% of responses correct per section before they are
            able to proceed. All questions are required.
          </p>

          {{#unless @questionnaire.isNew}}
            <label class="!text-base !text-carbon-70 !font-normal">
              <input
                type="checkbox"
                name="retake-revised"
                checked={{@questionnaire.forceRetakeOnRevision}}
                disabled={{@readOnly}}
                {{on "change" this.updateForceRetake}}
                data-test-retake-revised
              />
              Changes to this assessment require all visitors to retake it.
            </label>
          {{/unless}}
        </div>

        {{#if this.showLanguageSelector}}
          <div class="flex-0 whitespace-nowrap flex items-center">
            {{if @readOnly "View" "Edit"}}
            for
            <LanguageSelector
              @languages={{@availableLocales}}
              @onChange={{this.changeLocale}}
              @value={{this.selectedLocale}}
              @powerSelectClass="!mb-0"
              class="ml-1"
              data-test-questionnaire-locale
            />
          </div>
        {{/if}}
      </div>

      <section class="w-3/4 mt-6">
        <SettingsPanel
          @isExpandable={{true}}
          @isOpen={{this.isSettingsPanelOpen}}
          data-test-questionnaire-settings
          as |panel|
        >
          <panel.header
            @showCancel={{false}}
            {{on "click" this.toggleSettingsCollapsed}}
            class="hover:cursor-pointer"
            as |head|
          >
            <head.title>
              <header class="!font-normal text-lg">
                <strong>Setup</strong>
              </header>
            </head.title>
            <head.controls>
              <button type="button" data-test-expand-collapse-toggle>
                {{#if panel.isOpen}}
                  {{svgJar "chevron-up" height="10" width="10"}}
                {{else}}
                  {{svgJar "chevron-down" height="10" width="10"}}
                {{/if}}
              </button>
            </head.controls>
          </panel.header>

          <panel.body>
            <QuestionnaireSettingsForm
              @fetchFlowsPage={{@fetchFlowsPage}}
              @flows={{@flows}}
              @onChange={{this.addChangedModels}}
              @questionnaire={{@questionnaire}}
              @readOnly={{@readOnly}}
              @updateSelectedFlows={{this.updateAssignedFlows}}
              data-test-questionnaire-settings-form
            />
          </panel.body>
        </SettingsPanel>
      </section>

      {{#each this.sections as |section|}}
        <Section
          @canDeleteSection={{this.hasMultipleSections}}
          @defaultLocale={{@defaultLocale}}
          @deleteSection={{this.deleteSection}}
          @onChange={{this.addChangedModels}}
          @readOnly={{@readOnly}}
          @selectedLocale={{this.selectedLocale}}
          @section={{section}}
          @validator={{this.validator}}
          class="w-3/4 mt-6"
        />
      {{/each}}

      <div class="mt-4">
        {{#if this.showAddSectionButton}}
          <button type="button" class="btn btn--outline" {{on "click" this.addSection}} data-test-add-section>
            {{svgJar "plus-round"}}
            Add section
          </button>
        {{/if}}
      </div>
    </form>

    {{#if this.isShowingDeleteConfirmation}}
      <ConfirmationModal
        @closeButtonClass="btn--outline"
        @ctaText="Delete"
        @handleTaskAction={{this.deleteQuestionnaire}}
        @onClose={{this.hideDeleteConfirmation}}
        @showTopRightCloseButton={{true}}
        @title="Are you sure you want to delete {{@questionnaire.title}}?"
      >
        <p class="pt-8 !mb-0">You will not be able to recover it.</p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
